import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import type { AdminBroadcastCategory } from '../utils/notificationCategories';

export interface Nudge {
    category: 'photos' | 'share' | 'bookings' | 'friends';
    message: string;
    tripId?: string;
    tripTitle?: string;
    severity?: 'info' | 'warning';
}

export interface AdminBroadcast {
    notification_id: string;
    message: string;
    severity: 'info' | 'warning';
    deep_link: string | null;
    sent_at: string;
    /** Phase 1 (#211): added to the backend response. */
    category: AdminBroadcastCategory;
    /**
     * Phase 1 (#211): server-authoritative for click-only categories.
     * Frontend uses category to decide UI; bypass_preferences is exposed
     * for completeness but not currently consumed by the bell.
     */
    bypass_preferences: boolean;
}

/**
 * Phase 5 (#211): system-generated notifications (initially travel reminders).
 * Distinct from AdminBroadcast (admin-composed) — different table, different
 * gating, but same /notifications/{id}/click and /dismiss endpoints (the
 * backend branches on table lookup, the frontend doesn't need to know).
 */
export interface LifecycleNotification {
    notification_id: string;
    category: 'travel_reminder' | 'peek_notification';
    message: string;
    deep_link: string | null;
    severity: 'info' | 'warning';
    sent_at: string;
    trip_id: string | null;
    interval_days: number | null;
}

interface UseNudgesReturn {
    nudges: Nudge[];
    admin_broadcasts: AdminBroadcast[];
    lifecycle_notifications: LifecycleNotification[];
    loading: boolean;
    error: string | null;
    /** Re-fetch from the server. Useful after a mutation where you want true state. */
    refetch: () => Promise<void>;
    /**
     * Optimistically remove an admin broadcast from local state.
     * Call this after a successful POST /click or /dismiss so the bell updates
     * immediately without a round-trip. The next refetch will return canonical state.
     */
    dismissBroadcast: (notificationId: string) => void;
    /**
     * Phase 5 (#211): same optimistic-removal pattern, but for lifecycle
     * notifications. Backend dismiss endpoint is shared with admin broadcasts;
     * this is purely a local state update.
     */
    dismissLifecycle: (notificationId: string) => void;
}

export const useNudges = (): UseNudgesReturn => {
    const [nudges, setNudges] = useState<Nudge[]>([]);
    const [adminBroadcasts, setAdminBroadcasts] = useState<AdminBroadcast[]>([]);
    const [lifecycleNotifications, setLifecycleNotifications] = useState<LifecycleNotification[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchNudges = useCallback(async () => {
        try {
            const response = await api.get('/users/me/nudges');
            const data = await response.json();

            if (response.ok && data.success) {
                setNudges(data.nudges || []);
                setAdminBroadcasts(data.admin_broadcasts || []);
                setLifecycleNotifications(data.lifecycle_notifications || []);
                setError(null);
            } else {
                setError(data.error || 'Failed to load nudges');
            }
        } catch (err) {
            // Network errors / endpoint unavailable: silently fail.
            // No nudges shown; user experience degrades gracefully.
            console.log('Nudges API not available');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            await fetchNudges();
            if (cancelled) return;
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [fetchNudges]);

    const dismissBroadcast = useCallback((notificationId: string) => {
        setAdminBroadcasts(prev => prev.filter(b => b.notification_id !== notificationId));
    }, []);

    const dismissLifecycle = useCallback((notificationId: string) => {
        setLifecycleNotifications(prev => prev.filter(l => l.notification_id !== notificationId));
    }, []);

    return {
        nudges,
        admin_broadcasts: adminBroadcasts,
        lifecycle_notifications: lifecycleNotifications,
        loading,
        error,
        refetch: fetchNudges,
        dismissBroadcast,
        dismissLifecycle,
    };
};
